/* eslint-disable no-console */
/**
 * Récupération automatique des ChunkLoadError.
 *
 * SYMPTÔME : un écran blanc, ou un bouton qui ne fait plus rien, avec dans la
 * console « Loading chunk N failed » / ChunkLoadError.
 *
 * CAUSE : l'application tourne sur un index.html périmé (servi par un service
 * worker ou un cache HTTP) qui référence des chunks hachés - app-3f2a1b.js - * que le déploiement en cours a remplacés. Le serveur répond 404, webpack lève
 * une ChunkLoadError, et toute navigation vers une vue chargée à la demande
 * échoue. Aucun gestionnaire n'existait : l'utilisateur restait bloqué.
 *
 * TRAITEMENT : exactement celui du contrôle de version - désinscrire le service
 * worker, vider les Cache Storage, recharger sur une URL neuve. IndexedDB n'est
 * jamais touchée.
 *
 * GARDE-FOU : une seule récupération par onglet (sessionStorage). Si le
 * rechargement ramène la même erreur, la cause n'est pas le cache et boucler
 * transformerait une panne en déni de service. On avertit alors sans recharger.
 */

import { purgerEtRecharger } from '@/service/appVersionService';

const CLE_SESSION = 'hsig-chunk-recovery';

function estChunkLoadError(erreur, message) {
    if (erreur && erreur.name === 'ChunkLoadError') return true;

    const texte = String(
        message || (erreur && (erreur.message || erreur)) || ''
    );
    return /ChunkLoadError|Loading chunk \S+ failed|Loading CSS chunk/i.test(texte);
}

function dejaTente() {
    try {
        return window.sessionStorage.getItem(CLE_SESSION) === '1';
    } catch (e) {
        // sessionStorage indisponible : on préfère ne pas recharger du tout
        // plutôt que risquer une boucle infinie de rechargements.
        return true;
    }
}

function marquerTentative() {
    try {
        window.sessionStorage.setItem(CLE_SESSION, '1');
    } catch (e) { /* mode privé */ }
}

function traiter(erreur, message) {
    if (!estChunkLoadError(erreur, message)) return;

    if (dejaTente()) {
        console.error(
            '[chunk] ChunkLoadError persistante APRÈS récupération. Le cache n’est pas en cause : ' +
            'vérifier que le déploiement est complet et que les fichiers de /js/ sont accessibles.'
        );
        return;
    }

    console.error('[chunk] ChunkLoadError détectée - bundle périmé. Purge du cache et rechargement.');
    marquerTentative();

    // purgerEtRecharger et NON appliquerMiseAJour : aucune version n'est visée
    // ici, et en inscrire une fausse ferait afficher un échec de mise à jour
    // imaginaire au démarrage suivant. "chunk" n'est qu'une étiquette dans le
    // paramètre anti-cache de l'URL.
    purgerEtRecharger('chunk').catch(err => {
        console.error('[chunk] Récupération impossible', err);
    });
}

function installerRecuperationChunks() {
    // Chargement d'un chunk par <script> : remonte en 'error' global.
    window.addEventListener('error', (e) => traiter(e.error, e.message));

    // import() dynamique de vue-router : remonte en promesse rejetée.
    window.addEventListener('unhandledrejection', (e) => traiter(e.reason, ''));
}

export { installerRecuperationChunks, estChunkLoadError };
